import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { vendorApi } from '../../services/api';
import { Package, Search } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import TopBar from '../../components/TopBar';

export default function Resources() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const { data: vendorData, error } = useQuery({
    queryKey: ['vendor', 'me'],
    queryFn: async () => {
      const response = await vendorApi.me();
      return response.data;
    },
  });

  const { data: resourcesData, isLoading } = useQuery({
    queryKey: ['vendor', 'resources'],
    queryFn: async () => {
      const response = await vendorApi.getResources();
      return response.data;
    },
  });

  useEffect(() => {
    if (error) {
      navigate('/login');
    }
  }, [error, navigate]);

  const handleLogout = async () => {
    try {
      await vendorApi.logout();
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
      navigate('/login');
    }
  };

  const handleRowClick = (systemId: string) => {
    navigate(`/resources/${systemId}`);
  };

  const formatCurrency = (amount: number) => {
    return amount?.toLocaleString('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }) || '0 SEK';
  };

  // Filter resources based on search and type
  const filteredResources = resourcesData?.resources?.filter((resource: any) => {
    const matchesSearch =
      resource.no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.registrationNo?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = !typeFilter || resource.type === typeFilter;

    return matchesSearch && matchesType;
  }) || [];

  // Get unique types for filter
  const uniqueTypes = Array.from(new Set(
    resourcesData?.resources?.map((r: any) => r.type).filter(Boolean)
  ));

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Laddar resurser...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <Sidebar
        logoUrl={vendorData?.tenant?.logoUrl}
        companyName={vendorData?.tenant?.companyName}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <TopBar
          userName={vendorData?.vendor?.fullName || vendorData?.vendor?.username}
          onLogout={handleLogout}
        />

        {/* Page Content */}
        <main className="mx-auto w-full max-w-[1400px] flex-1 px-6 py-8 lg:px-10 lg:py-10">
          <div className="mb-6">
            <h1 className="font-display text-[1.35rem] leading-snug text-ink text-balance md:text-[1.6rem]">Resurser</h1>
            <p className="text-gray-600 mt-1">Visa och hantera dina resurser</p>
          </div>

          {/* Filters */}
          <div
            className="panel mb-6"
          >
            <div className="bg-surface p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Sök efter resursnr, namn, reg.nr..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              {/* Type Filter */}
              <div>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Alla typer</option>
                  {uniqueTypes.map((type: any) => (
                    <option key={type} value={type}>
                      Typ {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Results Count */}
            <div className="mt-4 text-sm text-gray-600">
              Visar {filteredResources.length} av {resourcesData?.count || 0} resurser
            </div>
            </div>
          </div>

          {/* Resources Table */}
          <div
            className="panel"
          >
            <div className="bg-surface overflow-hidden">
            {filteredResources.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Resursnr
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Namn
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Typ
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Reg.nr
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Belopp Senaste 12 Mån
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Belopp 24-12 Mån Sedan
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredResources.map((resource: any) => (
                      <tr
                        key={resource.systemId}
                        onClick={() => handleRowClick(resource.systemId)}
                        className="hover:bg-blue-50 cursor-pointer transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-mono font-medium text-gray-900">
                            {resource.no}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-900">{resource.name}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {resource.type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600 font-mono">
                            {resource.registrationNo || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className="text-sm font-medium text-gray-900">
                            {formatCurrency(resource.amountLast12Months || 0)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className="text-sm text-gray-600">
                            {formatCurrency(resource.amount24to12MonthsAgo || 0)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {resource.blocked ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Spärrad
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Aktiv
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="mb-2 text-base font-semibold text-ink">Inga resurser hittades</h3>
                <p className="text-gray-600">
                  {searchTerm || typeFilter
                    ? 'Prova att justera dina sök- eller filterkriterier'
                    : 'Inga resurser tillgängliga för ditt konto'}
                </p>
              </div>
            )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
